import { InputErrorMessage } from '@savvy-kit/components/input-error-message';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@savvy-kit/components/input-group';
import { Label } from '@savvy-kit/components/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '@savvy-kit/components/tooltip';
import { cn } from '@savvy-kit/lib/utils';
import { InfoIcon } from 'lucide-react';

type InputFieldProps = Omit<React.ComponentProps<'input'>, 'size'> & {
  readonly id: string;
  readonly size?: 'sm' | 'default' | 'lg';
  readonly label?: string;
  readonly description?: string;
  readonly error?: boolean;
  readonly errorMessage?: string;
  readonly prependInputAddon?: React.ReactNode;
  readonly appendInputAddon?: React.ReactNode;
  readonly disabled?: boolean;
  readonly showInfoIcon?: boolean;
  readonly infoTooltipMessage?: string;
  // Sizing/layout for the field as a whole. `className` reaches the inner <input>,
  // so it cannot size the group — a flex-item parent needs this to stop the field
  // collapsing to its content width.
  readonly containerClassName?: string;
};

export function InputField({
  id,
  size,
  label,
  description,
  error,
  errorMessage,
  prependInputAddon,
  appendInputAddon,
  disabled,
  showInfoIcon,
  infoTooltipMessage,
  containerClassName,
  ...props
}: InputFieldProps) {
  const showErrorMessage = error && errorMessage;
  const showInfoTooltip = showInfoIcon && infoTooltipMessage;
  const showRequired = props.required === true;

  return (
    <div className={containerClassName}>
      {label && (
        <div className={cn('flex items-center gap-2 pb-2', size === 'sm' && 'pb-1')}>
          <Label htmlFor={id} className={cn(size === 'sm' && 'text-xs')}>
            {label}
          </Label>
          {showRequired ? (
            <span className={cn('text-destructive', size === 'sm' ? 'text-xs' : 'text-sm')}>*</span>
          ) : null}
          {showInfoTooltip && (
            <Tooltip>
              <TooltipTrigger render={<InfoIcon className="size-3" />} />
              <TooltipContent>
                <p>{infoTooltipMessage}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      )}

      <InputGroup size={size} data-disabled={disabled}>
        <InputGroupInput aria-invalid={error} id={id} disabled={disabled} {...props} />
        {prependInputAddon && <InputGroupAddon>{prependInputAddon}</InputGroupAddon>}
        {appendInputAddon && <InputGroupAddon align="inline-end">{appendInputAddon}</InputGroupAddon>}
      </InputGroup>

      <div className={cn('text-xs pt-1', !description && !showErrorMessage && 'hidden')}>
        {showErrorMessage && <InputErrorMessage message={errorMessage} />}
        {description && !showErrorMessage && <span className="text-secondary">{description}</span>}
      </div>
    </div>
  );
}

export default InputField;
